import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, Link } from 'react-router-dom'
import { Wallet, MapPin, ExternalLink } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Spinner } from 'components/ui/spinner'
import { ConsolidatedPortfolioFinancialsGrid } from 'features/Financials/components/ConsolidatedPortfolioFinancialsGrid'
import { FinancialsGrid } from 'features/Financials/components'
import {
	fetchPortfolioFinancials,
	selectPortfolioFinancials,
	selectPortfolioFinancialsLoading,
} from 'slices/portfolioFinancialsSlice'
import {
	fetchIncomeStatement,
	updateCell,
	selectIncomeStatement,
} from 'slices/incomeStatementSlice'

type LocationLite = {
	_id: string
	image?: string
	assetName?: string
}

/**
 * Portfolio Financials sub-page.
 *
 * Composes the consolidated read-only grid (rows = locations × cols = line items)
 * at the top with per-location editable FinancialsGrid sections below. After any
 * per-location cell update, refetches the consolidated grid so totals stay in sync.
 *
 * Backend: GET /api/portfolios/:portfolioId/financials (MYD-252)
 * Slice:   slices/portfolioFinancialsSlice (MYD-253)
 * Grid:    features/Financials/components/ConsolidatedPortfolioFinancialsGrid (MYD-254)
 */
const PortfolioFinancials = () => {
	const dispatch = useDispatch()
	const { id } = useParams<{ id: string }>()
	const portfolioId = id || ''

	const portfolio = useSelector((state: any) => state.portfolios.entities[portfolioId])
	const data = useSelector((state: any) => selectPortfolioFinancials(state, portfolioId))
	const isLoading = useSelector((state: any) =>
		selectPortfolioFinancialsLoading(state, portfolioId),
	)

	const locationsList: LocationLite[] = useMemo(() => {
		return (portfolio?.locations || [])
			.map((entry: any) => entry?.location)
			.filter(Boolean)
	}, [portfolio?.locations])

	// On mount: fetch consolidated grid + every location's IncomeStatement in parallel.
	useEffect(() => {
		if (!portfolioId) return
		dispatch(fetchPortfolioFinancials(portfolioId) as any)
		for (const loc of locationsList) {
			dispatch(fetchIncomeStatement({ locationId: loc._id }) as any)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolioId, locationsList.length])

	if (!portfolio) return null

	const showInitialSpinner = isLoading && !data

	return (
		<div className="space-y-8">
			<header>
				<div className="flex items-center gap-2">
					<Wallet className="w-6 h-6 text-[#0080fe]" />
					<h2 className="text-2xl font-bold m-0">Financials</h2>
				</div>
				<p className="text-sm text-gray-600 mt-1">
					Consolidated roll-up of all locations in this portfolio. Edit any per-location detail
					below to update both views.
				</p>
			</header>

			{/* Consolidated read-only grid */}
			{showInitialSpinner ? (
				<Spinner containerHeight="200px" type="circular" />
			) : (
				<ConsolidatedPortfolioFinancialsGrid
					data={data || { locations: [], lineItems: [] }}
					isLoading={false}
				/>
			)}

			{/* Per-location editable detail */}
			{locationsList.length > 0 && (
				<section className="space-y-3">
					<div className="flex items-center gap-2 pt-4 border-t border-gray-200">
						<MapPin className="w-4 h-4 text-gray-500" />
						<h3 className="text-base font-semibold text-gray-700 m-0">Location Detail</h3>
					</div>
					<div className="space-y-6">
						{locationsList.map(loc => (
							<LocationFinancialsSection
								key={loc._id}
								location={loc}
								portfolioId={portfolioId}
							/>
						))}
					</div>
				</section>
			)}
		</div>
	)
}

type LocationFinancialsSectionProps = {
	location: LocationLite
	portfolioId: string
}

const LocationFinancialsSection = ({ location, portfolioId }: LocationFinancialsSectionProps) => {
	const dispatch = useDispatch()
	const incomeStatement = useSelector((state: any) =>
		selectIncomeStatement(state, location._id),
	)

	const handleCellChange = useCallback(
		(lineItemId: string, quarterId: string, value: number) => {
			dispatch(
				updateCell({
					locationId: location._id,
					lineItemId,
					quarterId,
					value,
				}) as any,
			)
				.then(() => {
					// Refresh consolidated rollup so totals reflect the updated cell.
					dispatch(fetchPortfolioFinancials(portfolioId) as any)
				})
				.catch(() => {
					// Silent — the slice surfaces the error via its own state.
				})
		},
		[dispatch, location._id, portfolioId],
	)

	const noop = useCallback(() => {}, [])

	return (
		<div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
			<div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
				<Avatar className="h-8 w-8 flex-shrink-0">
					<AvatarImage src={location.image} alt={location.assetName} />
					<AvatarFallback className="text-xs">
						{location.assetName?.charAt(0)?.toUpperCase() || 'L'}
					</AvatarFallback>
				</Avatar>
				<span className="text-sm font-semibold text-gray-900 truncate min-w-0">
					{location.assetName || 'Untitled'}
				</span>
				<Link
					to={`/locations/${location._id}/financials`}
					className="ml-auto text-xs text-[#0080fe] hover:underline flex items-center gap-1 flex-shrink-0"
					title="Open this location's full Financials page"
				>
					Open in Location
					<ExternalLink className="w-3 h-3" />
				</Link>
			</div>
			<div className="p-4">
				{incomeStatement ? (
					<FinancialsGrid
						incomeStatement={incomeStatement}
						onCellChange={handleCellChange}
						onLineItemReorder={noop}
						onLineItemDelete={noop}
						onQuarterDelete={noop}
					/>
				) : (
					<p className="text-sm text-gray-500 italic">
						No financials set up yet for this location.{' '}
						<Link
							to={`/locations/${location._id}/financials`}
							className="text-[#0080fe] hover:underline"
						>
							Set them up in the Location's Financials tab
						</Link>
						.
					</p>
				)}
			</div>
		</div>
	)
}

export default PortfolioFinancials
